/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import Container from '@mui/material/Container';
import Button from '@Button';
import Text from '@Text';
import FetchStatus from '../../Data/FetchStatus';
import { selectUpstreamLocationStepKey } from '../../Helpers/clientApiJourneyPosition.helpers';
import {
  goToNextScreen,
  goToPreviousScreen,
  setSelectedSlot,
  setStepState,
} from '../../Lib/redux/slices/clientApiJourney';
import { useWidgetHeightRefresh } from '../../Hooks/useWidgetHeightRefresh/useWidgetHeightRefresh';
import { useFallThroughToNormalLead } from '../../Hooks/useFallThroughToNormalLead';
import { FALL_THROUGH_REASONS } from './clientApiJourney.constants';
import NearbyBranch from './NearbyBranch';
import AppointmentAccordion, {
  EXPAND_TRANSITION_MS,
} from './AppointmentAccordion';
import makeSlotPickerStyles from './makeSlotPickerStyles';
import {
  getById,
  getByTestId,
  outerHeight,
  useOlbScrollMaxHeight,
  journeyProgressHeight,
} from './clientApiLayout.helpers';
import useClientApiCopy from './copy/useClientApiCopy';

const SlotPicker = ({ stepKey, componentId }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = makeSlotPickerStyles({ theme });
  const copy = useClientApiCopy();
  const cardsRef = useRef(null);
  const [accordionSettled, setAccordionSettled] = useState(0);
  const [accordionToggleCount, setAccordionToggleCount] = useState(0);

  const whatToExpect = useSelector(
    (store) =>
      store.components[`component_${componentId}`]?.component
        ?.additionalSettings?.what_to_expect
  );
  const accordionBody =
    typeof whatToExpect === 'string' && whatToExpect.trim() !== ''
      ? whatToExpect
      : null;

  const selectedLocation = useSelector(
    (store) => store.clientApiJourney.location?.selected
  );
  const locations = useSelector(
    (store) => store.clientApiJourney.location?.list
  );
  const isDynamicHeightEnabled = useSelector(
    (store) => store.optionalFormConfig?.isDynamicHeightEnabled
  );
  const selectedSlot = useSelector(
    (store) => store.clientApiJourney.appointment?.selectedSlot
  );
  const locationStepKey = useSelector(selectUpstreamLocationStepKey);
  const chosenSlotsStatus = useSelector(
    (store) =>
      store.clientApiJourney.location?.slotsByLocation?.[selectedLocation?.id]
        ?.slotsStatus
  );

  // The card holding the picked slot starts expanded, so returning from Review
  // reopens it; otherwise the chosen store. Only one card is open at a time.
  const [expandedBranchId, setExpandedBranchId] = useState(
    selectedSlot?.locationId ?? selectedLocation?.id ?? null
  );

  // expandedBranchId is intentionally omitted — the expand/collapse height
  // re-check is driven (post-animation) by each NearbyBranch instead.
  useWidgetHeightRefresh([chosenSlotsStatus, selectedSlot]);

  // If the chosen store's slot lookup fails, fall through to a normal lead.
  useFallThroughToNormalLead({
    shouldFallThrough: chosenSlotsStatus === FetchStatus.FAILED,
    reason: FALL_THROUGH_REASONS.SLOTS_LOOKUP_FAILED,
  });

  const handleOpenBranch = (branchId) => {
    setExpandedBranchId(branchId);
    // Opening a different clinic clears the slot picked in the previous card, so
    // Continue cannot book a slot the open card no longer shows as selected.
    if (selectedSlot && selectedSlot.locationId !== branchId) {
      dispatch(setSelectedSlot({ selectedSlot: null }));
    }
  };

  const handleAccordionToggle = () => {
    setAccordionToggleCount((n) => n + 1);
  };

  // Re-measure once the expand/collapse animation has settled. Keyed on the
  // toggle count so the timer is cleared if the screen unmounts or the user
  // toggles again before it fires.
  useEffect(() => {
    if (accordionToggleCount === 0) {
      return undefined;
    }
    const timer = setTimeout(
      () => setAccordionSettled((n) => n + 1),
      EXPAND_TRANSITION_MS + 50
    );
    return () => clearTimeout(timer);
  }, [accordionToggleCount]);

  const handleBack = () => {
    // Keep selectedLocation so the store list still highlights the chosen store
    // (the user can pick another there); only the slot is reset.
    dispatch(setSelectedSlot({ selectedSlot: null }));
    dispatch(goToPreviousScreen());
  };

  const handleContinue = () => {
    dispatch(
      setStepState({
        stepKey,
        state: { start: selectedSlot.start, end: selectedSlot.end },
      })
    );
    dispatch(goToNextScreen());
  };

  const nearbyLocations = (locations ?? []).filter(
    (location) => location.id !== selectedLocation?.id
  );
  // The chosen store is rendered as the first (initially expanded) card.
  const stores = selectedLocation
    ? [selectedLocation, ...nearbyLocations]
    : nearbyLocations;

  // Bound the card region to the form's remaining space so it scrolls while the
  // heading + sticky Continue footer stay visible.
  useOlbScrollMaxHeight(
    cardsRef,
    () => {
      const formContent = getById('formContentContainer');
      if (!formContent) return 0;
      return (
        formContent.clientHeight -
        (formContent.children?.[0]?.clientHeight ?? 0) -
        journeyProgressHeight() -
        outerHeight(getByTestId('slotPickerBackRow')) -
        outerHeight(getByTestId('slotPickerHeadingRow')) -
        outerHeight(getByTestId('slotPickerAccordionRow')) -
        outerHeight(getByTestId('slotPickerContinueRow')) -
        (getById('footerContainer')?.clientHeight ?? 0)
      );
    },
    [
      stores.length,
      selectedSlot,
      expandedBranchId,
      isDynamicHeightEnabled,
      accordionSettled,
    ],
    !isDynamicHeightEnabled
  );

  return (
    <Container css={classes.container} data-testid="slotPicker">
      <Box css={classes.backButtonContainer} data-testid="slotPickerBackRow">
        <MuiButton
          css={classes.toggleLink}
          onClick={handleBack}
          data-testid="slotPickerBack"
        >
          {copy.slot.back}
        </MuiButton>
      </Box>
      <Box
        css={[classes.heading, classes.slotPickerHeadingGap]}
        data-testid="slotPickerHeadingRow"
      >
        <Text variant="question">{copy.slot.heading}</Text>
      </Box>

      {accordionBody && (
        <Box data-testid="slotPickerAccordionRow">
          <AppointmentAccordion
            label={copy.slot.accordionLabel}
            body={accordionBody}
            onToggle={handleAccordionToggle}
          />
        </Box>
      )}

      <Box
        css={classes.cardScrollArea}
        ref={cardsRef}
        data-testid="slotPickerScrollRegion"
      >
        {stores.map((location) => (
          <NearbyBranch
            key={location.id}
            location={location}
            namePrefix={
              location.id === expandedBranchId ? copy.slot.bookingAt : ''
            }
            isExpanded={expandedBranchId === location.id}
            onOpen={handleOpenBranch}
            locationStepKey={locationStepKey}
          />
        ))}
      </Box>

      <Box css={classes.continueContainer} data-testid="slotPickerContinueRow">
        <Button
          label={copy.slot.continue}
          onClick={handleContinue}
          disabled={!selectedSlot}
          dataTestId="slotPickerContinue"
        />
      </Box>
    </Container>
  );
};

export default SlotPicker;
